import { useState, useCallback, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Banner,
  SkeletonBodyText,
  Modal,
  TextField,
  Divider,
  ProgressBar,
  List,
  ChoiceList,
  Spinner,
} from '@shopify/polaris';
import {
  useMetaConfig,
  useConfigureMeta,
  useToggleMeta,
  useTestMeta,
  useMetaEMQ,
  useRefreshEMQ,
  useDisconnectMeta,
  useConnectMeta,
  useMetaAssets,
  useSelectMetaAsset,
  useUpdateTestEventCode,
} from '../hooks/api';
import { useTranslation, useLanguage } from '../i18n/LanguageContext';
import { localeMap } from '../i18n/translations';
import { openOAuthWindow, navigateOAuth } from '../lib/auth';
import { useOAuthCallbackError } from '../hooks/useOAuthCallbackError';
import { GateBanner, type PageError } from '../components/GateBanner';

function StatusBadge({ status, t }: { status: string; t: ReturnType<typeof useTranslation> }) {
  switch (status) {
    case 'connected':
      return <Badge tone="success">{t.common.connected}</Badge>;
    case 'warning':
      return <Badge tone="warning">{t.common.warning}</Badge>;
    case 'error':
      return <Badge tone="critical">{t.common.error}</Badge>;
    case 'disabled':
      return <Badge>{t.common.disabled}</Badge>;
    default:
      return <Badge>{status}</Badge>;
  }
}

function EMQScore({ score, label }: { score: number; label: string }) {
  const getColor = () => {
    if (score >= 8) return 'success';
    if (score >= 6) return 'highlight';
    if (score >= 4) return 'highlight';
    return 'critical';
  };

  return (
    <BlockStack gap="100">
      <InlineStack align="space-between">
        <Text as="span" variant="bodySm">
          {label}
        </Text>
        <Text as="span" variant="bodySm" fontWeight="semibold">
          {score.toFixed(1)}/10
        </Text>
      </InlineStack>
      <ProgressBar progress={score * 10} tone={getColor()} size="small" />
    </BlockStack>
  );
}

export function Meta() {
  const { data: config, isLoading } = useMetaConfig();
  const configureMutation = useConfigureMeta();
  const toggleMutation = useToggleMeta();
  const testMutation = useTestMeta();
  const { refetch: fetchEMQ } = useMetaEMQ();
  const refreshEMQMutation = useRefreshEMQ();
  const disconnectMutation = useDisconnectMeta();
  const t = useTranslation();
  const { language } = useLanguage();

  const connectMutation = useConnectMeta();
  const selectAssetMutation = useSelectMetaAsset();
  const updateTestCodeMutation = useUpdateTestEventCode();
  // Editable test-event-code in the CONNECTED state (the OAuth path never goes
  // through the manual configure form, so this is its only way to set one).
  const [connectedTestCode, setConnectedTestCode] = useState<string | null>(null);

  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [selectedPixel, setSelectedPixel] = useState('');
  const [pixelId, setPixelId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [testEventCode, setTestEventCode] = useState('');
  const [testResult, setTestResult] = useState<{ success?: boolean; message?: string } | null>(null);
  const [configError, setConfigError] = useState<PageError>(null);
  const [connectError, setConnectError] = useState<PageError>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthErr = params.get('error');
    if (oauthErr) {
      setConnectError(
        /access_denied|user_denied|cancell?ed/i.test(oauthErr)
          ? 'The Facebook connection was canceled. Nothing was changed - you can connect again anytime.'
          : `The Facebook connection could not be completed (${oauthErr.replace(/^[a-z0-9_]*?_/, '')}). Please try again.`
      );
    }
    if (params.get('step') === 'select-pixel' || params.get('meta') === 'connected') {
      setShowPicker(true);
    }
  }, []);

  const assetsQuery = useMetaAssets(showPicker);

  /*
    Graph refused an edge AND that refusal left us with nothing to offer.

    Both halves matter. The check is applied per endpoint, so a partial refusal
    can still return pixels - through the Business sweep when the ad-account
    one is rejected, say. In that case the merchant has a real choice to make
    and must not be pushed towards pasting a token instead.
  */
  const metaEnumerationBlocked =
    !assetsQuery.isLoading &&
    !assetsQuery.isError &&
    assetsQuery.data?.blocked === true &&
    (assetsQuery.data?.pixels.length ?? 0) === 0;

  const handleConnect = useCallback(async () => {
    setConnectError(null);
    const win = openOAuthWindow();
    try {
      const { url } = await connectMutation.mutateAsync();
      navigateOAuth(win, url);
    } catch (error: any) {
      win?.close();
      setConnectError(error?.message ? error : 'Could not start the Facebook connection.');
    }
  }, [connectMutation]);

  const closePicker = useCallback(() => {
    setShowPicker(false);
    setSelectedPixel('');
    const params = new URLSearchParams(window.location.search);
    params.delete('step');
    params.delete('meta');
    params.delete('platform');
    const clean = window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
    window.history.replaceState({}, '', clean);
  }, []);

  /**
   * Leave the one-click flow for the paste-your-own-token form.
   *
   * This is the exit the picker was missing. Meta's Conversions API needs
   * neither App Review nor any permission ("Your app does not need to go
   * through App Review. You do not need to request any permissions."), so a
   * merchant whose pixel enumeration Graph refuses can still send every event
   * today - just not by the one-click route.
   */
  const switchToManual = useCallback(() => {
    closePicker();
    setShowConfigModal(true);
  }, [closePicker]);

  const handleSelectAsset = useCallback(async () => {
    if (!selectedPixel) return;
    try {
      await selectAssetMutation.mutateAsync({ pixelId: selectedPixel });
      closePicker();
    } catch (error: any) {
      setConnectError(error?.message ? error : 'Could not finish the connection.');
    }
  }, [selectedPixel, selectAssetMutation, closePicker]);

  const handleConfigure = useCallback(async () => {
    setConfigError(null);
    if (!pixelId || !accessToken) {
      setConfigError(t.meta.required);
      return;
    }

    try {
      await configureMutation.mutateAsync({
        pixelId,
        accessToken,
        testEventCode: testEventCode || undefined,
      });
      setShowConfigModal(false);
      setPixelId('');
      setAccessToken('');
      setTestEventCode('');
    } catch (error: any) {
      setConfigError(error?.message ? error : t.meta.testFailed);
    }
  }, [pixelId, accessToken, testEventCode, configureMutation, t]);

  const [actionError, setActionError] = useState<PageError>(null);
  // Surface OAuth-callback failures delivered via ?error= (e.g. partial consent).
  useOAuthCallbackError(setActionError);

  const handleToggle = useCallback(
    async (enabled: boolean) => {
      setActionError(null);
      try {
        await toggleMutation.mutateAsync(enabled);
      } catch (error: any) {
        console.error('Failed to toggle:', error);
        setActionError(error?.message ? error : t.meta.actionFailed);
      }
    },
    [toggleMutation, t]
  );

  const handleTest = useCallback(async () => {
    setTestResult(null);
    setActionError(null);
    try {
      const result: any = await testMutation.mutateAsync();
      setTestResult({
        success: true,
        message: `${t.meta.testSuccess} ${result.eventsReceived}`,
      });
    } catch (error: any) {
      setTestResult({ success: false, message: error.message || t.meta.testFailed });
    }
  }, [testMutation, t]);

  const [emqPermissionHint, setEmqPermissionHint] = useState(false);
  const handleRefreshEMQ = useCallback(async () => {
    setActionError(null);
    setEmqPermissionHint(false);
    try {
      const result: any = await refreshEMQMutation.mutateAsync();
      // Write-only dataset tokens can post events but not READ event stats -
      // that's token scoping, not a channel problem. Guide instead of erroring.
      if (result?.summary?.permissionDenied) {
        setEmqPermissionHint(true);
        return;
      }
      fetchEMQ();
    } catch (error: any) {
      console.error('Failed to refresh EMQ:', error);
      setActionError(error?.message ? error : t.meta.actionFailed);
    }
  }, [refreshEMQMutation, fetchEMQ, t]);

  const handleDisconnect = useCallback(async () => {
    setActionError(null);
    try {
      await disconnectMutation.mutateAsync();
      setShowDisconnectModal(false);
    } catch (error: any) {
      console.error('Failed to disconnect:', error);
      setActionError(error?.message ? error : t.meta.actionFailed);
    }
  }, [disconnectMutation, t]);

  if (isLoading) {
    return (
      <Page title={t.meta.title}>
        <Layout>
          <Layout.Section>
            <Card>
              <SkeletonBodyText lines={6} />
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title={t.meta.title}
      subtitle={t.meta.subtitle}
      primaryAction={
        config?.connected
          ? {
              content: config.isEnabled ? t.meta.disableTracking : t.meta.enableTracking,
              // Disabling pauses delivery to Meta - confirm first (capture
              // continues; re-enable backfills). Enabling is direct.
              onAction: () => (config.isEnabled ? setShowDisableModal(true) : handleToggle(true)),
              loading: toggleMutation.isPending,
            }
          : undefined
      }
    >
      <Layout>
        {/* Action Error Banner */}
        {actionError && (
          <Layout.Section>
            <GateBanner error={actionError} onDismiss={() => setActionError(null)} />
          </Layout.Section>
        )}

        {/* Connect Error Banner */}
        {connectError && (
          <Layout.Section>
            <GateBanner error={connectError} onDismiss={() => setConnectError(null)} />
          </Layout.Section>
        )}

        {/* Error Banner */}
        {config?.lastErrorMessage && (
          <Layout.Section>
            <Banner tone="warning" title={t.common.warning}>
              <p>{config.lastErrorMessage}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Test Result */}
        {testResult && (
          <Layout.Section>
            <Banner
              tone={testResult.success ? 'success' : 'critical'}
              onDismiss={() => setTestResult(null)}
            >
              <p>{testResult.message}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Connection Status */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingMd">
                  {t.meta.connectionStatus}
                </Text>
                {config?.connected && <StatusBadge status={config.status || 'connected'} t={t} />}
              </InlineStack>

              {config?.connected ? (
                <BlockStack gap="300">
                  <InlineStack gap="200">
                    <Text as="span" variant="bodyMd" tone="subdued">
                      {t.meta.pixelId}:
                    </Text>
                    <Text as="span" variant="bodyMd" fontWeight="semibold">
                      {config.pixelId}
                    </Text>
                  </InlineStack>

                  {/* Editable test-event-code (Events Manager > Test events):
                      events carrying it show up there within seconds - the
                      instant E2E verification lever. */}
                  <InlineStack gap="200" blockAlign="end" wrap={false}>
                    <div style={{ flexGrow: 1 }}>
                      <TextField
                        label={t.meta.testEventCodeOptional}
                        value={connectedTestCode ?? config.testEventCode ?? ''}
                        onChange={setConnectedTestCode}
                        autoComplete="off"
                        placeholder="TEST12345"
                      />
                    </div>
                    <Button
                      loading={updateTestCodeMutation.isPending}
                      disabled={connectedTestCode === null || connectedTestCode === (config.testEventCode ?? '')}
                      onClick={async () => {
                        try {
                          await updateTestCodeMutation.mutateAsync(connectedTestCode ?? '');
                          setConnectedTestCode(null);
                        } catch (error: any) {
                          setActionError(error?.message ? error : t.meta.actionFailed);
                        }
                      }}
                    >
                      {t.common.save}
                    </Button>
                  </InlineStack>

                  {config.lastSyncAt && (
                    <InlineStack gap="200">
                      <Text as="span" variant="bodyMd" tone="subdued">
                        {t.meta.lastSync}:
                      </Text>
                      <Text as="span" variant="bodyMd">
                        {new Date(config.lastSyncAt).toLocaleString(localeMap[language])}
                      </Text>
                    </InlineStack>
                  )}

                  <Divider />

                  <InlineStack gap="200">
                    <Button onClick={handleTest} loading={testMutation.isPending}>
                      {t.meta.sendTestEvent}
                    </Button>
                    <Button onClick={() => setShowConfigModal(true)}>
                      {t.meta.updateConfiguration}
                    </Button>
                    <Button
                      variant="plain"
                      tone="critical"
                      onClick={() => setShowDisconnectModal(true)}
                    >
                      {t.common.disconnect}
                    </Button>
                  </InlineStack>
                </BlockStack>
              ) : (
                <BlockStack gap="300">
                  <Text as="p" variant="bodyMd">
                    Connect Facebook in one click - we list your pixels and set up the Conversions
                    API for you, no Pixel ID or token to copy.
                  </Text>
                  <InlineStack gap="200">
                    <Button variant="primary" onClick={handleConnect} loading={connectMutation.isPending}>
                      Connect Facebook
                    </Button>
                    <Button onClick={() => setShowConfigModal(true)}>
                      Advanced: enter manually
                    </Button>
                  </InlineStack>
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Event Match Quality */}
        {config?.connected && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="h2" variant="headingMd">
                    {t.meta.emq}
                  </Text>
                  <Button
                    size="slim"
                    onClick={handleRefreshEMQ}
                    loading={refreshEMQMutation.isPending}
                  >
                    {t.common.refresh}
                  </Button>
                </InlineStack>

                <Text as="p" variant="bodySm" tone="subdued">
                  {t.meta.emqDesc}
                </Text>

                {emqPermissionHint && (
                  <Banner tone="info">
                    <p>
                      Your Conversions API token can send events but cannot read match-quality
                      stats. To unlock EMQ, generate a token with the Dataset Quality API
                      (Events Manager &gt; your dataset &gt; Settings &gt; Conversions API &gt;
                      "Set up with Dataset Quality API"), then paste it via Update
                      Configuration. Event delivery is unaffected either way.
                    </p>
                  </Banner>
                )}

                {config.emqStatus ? (
                  <BlockStack gap="300">
                    <InlineStack gap="200" align="space-between" blockAlign="center">
                      <Text as="p" variant="bodyMd" fontWeight="semibold">
                        {t.meta.overallScore}
                      </Text>
                      <Badge
                        tone={
                          config.emqStatus.overall === 'Excellent'
                            ? 'success'
                            : config.emqStatus.overall === 'Good'
                              ? 'info'
                              : 'warning'
                        }
                      >
                        {config.emqStatus.overall}
                      </Badge>
                    </InlineStack>

                    {config.eventMatchQuality && (
                      <BlockStack gap="200">
                        {Object.entries(config.eventMatchQuality).map(([event, score]) => (
                          <EMQScore key={event} label={event} score={score as number} />
                        ))}
                      </BlockStack>
                    )}

                    {config.emqStatus.recommendations &&
                      config.emqStatus.recommendations.length > 0 && (
                        <BlockStack gap="200">
                          <Text as="p" variant="bodyMd" fontWeight="semibold">
                            {t.meta.recommendations}
                          </Text>
                          <List>
                            {config.emqStatus.recommendations.map((rec, index) => (
                              <List.Item key={index}>{rec}</List.Item>
                            ))}
                          </List>
                        </BlockStack>
                      )}
                  </BlockStack>
                ) : (
                  <Text as="p" variant="bodyMd" tone="subdued">
                    {t.meta.emqNotAvailable}
                  </Text>
                )}
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* Setup Instructions */}
        {!config?.connected && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  {t.meta.setupInstructions}
                </Text>
                <BlockStack gap="200">
                  <Text as="p" variant="bodyMd">{t.meta.step1}</Text>
                  <Text as="p" variant="bodyMd">{t.meta.step2}</Text>
                  <Text as="p" variant="bodyMd">{t.meta.step3}</Text>
                  <Text as="p" variant="bodyMd">{t.meta.step4}</Text>
                  <Text as="p" variant="bodyMd">{t.meta.step5}</Text>
                </BlockStack>
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* Configuration Modal */}
        <Modal
          open={showConfigModal}
          onClose={() => {
            setShowConfigModal(false);
            setConfigError(null);
          }}
          title={t.meta.configureTitle}
          primaryAction={{
            content: t.meta.saveConfiguration,
            onAction: handleConfigure,
            loading: configureMutation.isPending,
          }}
          secondaryActions={[
            {
              content: t.common.cancel,
              onAction: () => {
                setShowConfigModal(false);
                setConfigError(null);
              },
            },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="400">
              {configError && (
                <GateBanner error={configError} />
              )}

              <TextField
                label={t.meta.pixelId}
                value={pixelId}
                onChange={setPixelId}
                placeholder={t.meta.pixelIdPlaceholder}
                autoComplete="off"
                helpText={t.meta.pixelIdHelp}
              />

              <TextField
                label={t.meta.accessToken}
                value={accessToken}
                onChange={setAccessToken}
                type="password"
                placeholder={t.meta.accessTokenPlaceholder}
                autoComplete="off"
                helpText={t.meta.accessTokenHelp}
              />

              <TextField
                label={t.meta.testEventCodeOptional}
                value={testEventCode}
                onChange={setTestEventCode}
                placeholder={t.meta.testEventCodePlaceholder}
                autoComplete="off"
                helpText={t.meta.testEventCodeHelp}
              />
            </BlockStack>
          </Modal.Section>
        </Modal>

        {/* Pixel Picker Modal (after one-click Connect) */}
        <Modal
          open={showPicker}
          onClose={closePicker}
          title="Choose your Meta pixel"
          /*
            When Graph refused to enumerate, "Finish setup" leads nowhere: there
            is nothing to select and no amount of reconnecting changes that.
            The only action that still works becomes the primary one.
          */
          primaryAction={
            metaEnumerationBlocked
              ? { content: 'Enter Pixel ID and token', onAction: switchToManual }
              : {
                  content: 'Finish setup',
                  onAction: handleSelectAsset,
                  loading: selectAssetMutation.isPending,
                  disabled: !selectedPixel,
                }
          }
          secondaryActions={[{ content: t.common.cancel, onAction: closePicker }]}
        >
          <Modal.Section>
            {assetsQuery.isLoading ? (
              <InlineStack gap="200" blockAlign="center">
                <Spinner size="small" accessibilityLabel="Loading pixels" />
                <Text as="span" variant="bodyMd">Loading your Meta pixels...</Text>
              </InlineStack>
            ) : assetsQuery.isError ? (
              <Banner tone="critical">
                <p>Could not load your pixels. The connection may have expired - click Connect again.</p>
              </Banner>
            ) : metaEnumerationBlocked ? (
              /*
                Facebook accepted the login and then refused to list the pixels.
                That is not an expired connection and reconnecting cannot fix
                it: it is Meta's Tech Provider check on our side, pending on
                our end, and invisible from the merchant's.

                Say so, and hand them the path that does work rather than a
                button that will fail again. Nothing here asks them to wait for
                us: the manual setup sends the exact same server-side events,
                and the connection survives the verification landing.
              */
              <Banner tone="warning" title="One-click setup is not available for this account yet">
                <BlockStack gap="200">
                  <Text as="p" variant="bodyMd">
                    Facebook accepted the login but would not list this account's pixels. Our
                    Meta review for reading advertiser assets is still in progress, so reconnecting
                    will not change the result.
                  </Text>
                  <Text as="p" variant="bodyMd">
                    Server-side tracking itself is unaffected. Paste your Pixel ID and a Conversions
                    API token instead and everything sends exactly the same way - you can find both
                    in{' '}
                    <a
                      href="https://business.facebook.com/events_manager2"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Meta Events Manager
                    </a>{' '}
                    under your dataset's Settings tab.
                  </Text>
                </BlockStack>
              </Banner>
            ) : (assetsQuery.data?.pixels.length ?? 0) === 0 ? (
              /*
                Name what we looked at instead of only saying we found nothing.
                "No pixels" has two causes the merchant cannot tell apart: no
                pixel exists, or they authorized an account that does not hold
                theirs. The second is the common one, and it is fixed by
                reconnecting with the right account, not by creating anything.
              */
              <Banner tone="warning" title="No Meta pixel found">
                <BlockStack gap="200">
                  {(assetsQuery.data?.accounts?.length ?? 0) > 0 ? (
                    <>
                      <Text as="p" variant="bodyMd">
                        We looked at {assetsQuery.data!.accounts.length} account
                        {assetsQuery.data!.accounts.length > 1 ? 's' : ''} and none of them has a
                        pixel:
                      </Text>
                      <List type="bullet">
                        {assetsQuery.data!.accounts.map((a) => (
                          <List.Item key={`${a.source}/${a.name}`}>
                            {a.name} ({a.source === 'business' ? 'Business' : 'ad account'})
                          </List.Item>
                        ))}
                      </List>
                      <Text as="p" variant="bodyMd">
                        If your pixel lives elsewhere, reconnect and pick that Business. Otherwise
                        create one in{' '}
                        <a
                          href="https://business.facebook.com/events_manager2"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Meta Events Manager
                        </a>
                        , then reconnect.
                      </Text>
                    </>
                  ) : (
                    <Text as="p" variant="bodyMd">
                      This Facebook account has no ad account or Business we can read. Check that
                      you authorized the right Business, then reconnect.
                    </Text>
                  )}
                </BlockStack>
              </Banner>
            ) : (
              <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                <ChoiceList
                  title="Pick the pixel to send conversions to."
                  choices={(assetsQuery.data?.pixels ?? []).map((p) => ({
                    label: `${p.pixelName} (${p.adAccountName})`,
                    value: p.pixelId,
                    // The identifier is the only value the merchant can match
                    // against Events Manager - pixel names repeat across
                    // accounts, and two called "Website" are not a choice.
                    helpText: p.pixelId,
                  }))}
                  selected={selectedPixel ? [selectedPixel] : []}
                  onChange={(v) => setSelectedPixel(v[0] ?? '')}
                />
              </div>
            )}
          </Modal.Section>
        </Modal>

        {/* Disable Confirmation Modal - capture continues, delivery pauses,
            re-enable re-delivers what was held (parity with Google Ads) */}
        <Modal
          open={showDisableModal}
          onClose={() => setShowDisableModal(false)}
          title={t.common.disableChannelTitle.replace('{channel}', 'Meta')}
          primaryAction={{
            content: t.meta.disableTracking,
            onAction: () => handleToggle(false),
            loading: toggleMutation.isPending,
            destructive: true,
          }}
          secondaryActions={[
            {
              content: t.common.cancel,
              onAction: () => setShowDisableModal(false),
            },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="200">
              <Text as="p" variant="bodyMd">{t.common.disableChannelBody.replace('{channel}', 'Meta')}</Text>
              <Text as="p" variant="bodyMd" tone="subdued">{t.common.disableChannelSafety.replace('{channel}', 'Meta')}</Text>
            </BlockStack>
          </Modal.Section>
        </Modal>

        {/* Disconnect Confirmation Modal */}
        <Modal
          open={showDisconnectModal}
          onClose={() => setShowDisconnectModal(false)}
          title={t.meta.disconnectTitle}
          primaryAction={{
            content: t.common.disconnect,
            onAction: handleDisconnect,
            loading: disconnectMutation.isPending,
            destructive: true,
          }}
          secondaryActions={[
            {
              content: t.common.cancel,
              onAction: () => setShowDisconnectModal(false),
            },
          ]}
        >
          <Modal.Section>
            <Text as="p" variant="bodyMd">
              {t.meta.disconnectConfirm}
            </Text>
          </Modal.Section>
        </Modal>
      </Layout>
    </Page>
  );
}
